import React, { useEffect, useMemo, useState } from "react";
import ForeignKeyField from "../ForeignKeyField";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import { Button } from "../ui/button";

const TECHNICAL_FIELDS = ["created_at", "updated_at", "deleted_at"];

const getResolvedType = (typeValue) => {
  if (Array.isArray(typeValue)) {
    return typeValue.find((value) => value !== "null") || "string";
  }
  return typeValue || "string";
};

export const getPrimaryKeyField = (schema) => {
  if (!schema?.properties) {
    return null;
  }
  for (const [key, props] of Object.entries(schema.properties)) {
    if (props.primary_key) {
      return key;
    }
  }
  return null;
};

export const createEmptyFormData = (schema) => {
  const nextData = {};
  Object.entries(schema?.properties || {}).forEach(([key, props]) => {
    const fieldType = getResolvedType(props.type);
    if (fieldType === "boolean") {
      nextData[key] = false;
      return;
    }
    nextData[key] = null;
  });
  return nextData;
};

export const buildRecordPayload = (schema, formData) => {
  const payload = {};
  Object.entries(schema?.properties || {}).forEach(([key, props]) => {
    if (formData[key] === undefined) {
      return;
    }
    const fieldType = getResolvedType(props.type);
    if (formData[key] === "" && fieldType !== "string") {
      payload[key] = null;
      return;
    }
    if (fieldType === "boolean") {
      payload[key] = Boolean(formData[key]);
      return;
    }
    if (fieldType === "integer" && formData[key] !== null && formData[key] !== undefined && formData[key] !== "") {
      payload[key] = Number(formData[key]);
      return;
    }
    if (fieldType === "number" && formData[key] !== null && formData[key] !== undefined && formData[key] !== "") {
      payload[key] = Number(formData[key]);
      return;
    }
    payload[key] = formData[key];
  });
  return payload;
};

function TableRecordForm({
  tableName,
  schema,
  initialData,
  error,
  saving,
  onSubmit,
  onCancel,
  onDelete,
  submitLabel,
  cancelLabel = "Cancel",
  compact = false,
}) {
  const [formData, setFormData] = useState({});
  const [currentForeignValues, setCurrentForeignValues] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const primaryKey = useMemo(() => getPrimaryKeyField(schema), [schema]);

  useEffect(() => {
    if (!schema) {
      setFormData({});
      return;
    }
    if (initialData) {
      setFormData({ ...createEmptyFormData(schema), ...initialData });
      return;
    }
    setFormData(createEmptyFormData(schema));
  }, [schema, initialData]);

  const handleChange = (event) => {
    let { name, value } = event.target;
    if (event.target.type === "checkbox") {
      value = event.target.checked;
    }
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleForeignKeyChange = (fieldName, value, selectedItem) => {
    setFormData((previous) => ({
      ...previous,
      [fieldName]: value,
    }));
    setCurrentForeignValues((previous) => ({
      ...previous,
      [fieldName]: selectedItem,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!schema) {
      return;
    }
    await onSubmit(buildRecordPayload(schema, formData), {
      isNewRecord: !formData[primaryKey],
      primaryKey,
      recordId: formData[primaryKey],
    });
  };

  const footerClasses = compact
    ? "mt-5 flex items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-gray-700"
    : "mt-6 flex items-center justify-between gap-3 border-t border-gray-200 bg-white pt-4 dark:border-gray-700 dark:bg-gray-800";

  return (
    <>
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <div className={`space-y-4 overflow-y-auto pr-1 ${compact ? "" : "pb-4"}`}>
          {schema &&
            Object.entries(schema.properties || {}).map(([key, props]) => {
              if (TECHNICAL_FIELDS.includes(key)) {
                return null;
              }
              const resolvedType = getResolvedType(props.type);
              const isPrimaryKey = props.primary_key;
              const isNewRecord = !initialData;
              if (isPrimaryKey && isNewRecord) {
                return null;
              }
              const isReadOnly = Boolean(isPrimaryKey);
              const isRequired = schema.required?.includes(key) && !isPrimaryKey;
              const hasForeignKey = props.foreign_key && typeof props.foreign_key === "object";
              const hasEnum = Array.isArray(props.enum) && props.enum.length > 0;

              if (hasForeignKey) {
                return (
                  <div key={key}>
                    <ForeignKeyField
                      name={key}
                      schema={props}
                      value={formData[key] || ""}
                      onChange={handleForeignKeyChange}
                      currentValue={currentForeignValues[key]}
                      isLoading={false}
                      error={null}
                      required={isRequired}
                    />
                  </div>
                );
              }

              if (hasEnum) {
                return (
                  <div key={key} className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {key}
                      {isRequired && <span className="ml-1 text-red-500">*</span>}
                    </label>
                    <select
                      name={key}
                      value={formData[key] || ""}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      required={isRequired && !isReadOnly}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    >
                      <option value="">Select...</option>
                      {props.enum.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {props.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{props.description}</p>
                    )}
                  </div>
                );
              }

              if (resolvedType === "boolean") {
                return (
                  <div key={key} className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {key}
                      {isRequired && <span className="ml-1 text-red-500">*</span>}
                    </label>
                    <label className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
                      <input
                        type="checkbox"
                        name={key}
                        checked={Boolean(formData[key])}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <span>Enabled</span>
                    </label>
                    {props.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{props.description}</p>
                    )}
                  </div>
                );
              }

              return (
                <div key={key} className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {key}
                    {isPrimaryKey ? " (Primary Key)" : ""}
                    {isRequired && <span className="ml-1 text-red-500">*</span>}
                  </label>
                  <input
                    type={resolvedType === "integer" || resolvedType === "number" ? "number" : "text"}
                    name={key}
                    value={formData[key] ?? ""}
                    onChange={handleChange}
                    readOnly={isReadOnly}
                    required={isRequired && !isReadOnly}
                    className={`w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-blue-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 ${isReadOnly ? "cursor-not-allowed bg-gray-50 dark:bg-gray-950" : ""}`}
                  />
                  {props.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">{props.description}</p>
                  )}
                </div>
              );
            })}
        </div>

        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">
            {error}
          </div>
        )}

        <div className={footerClasses}>
          <div>
            {initialData && onDelete && (
              <Button type="button" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40" onClick={() => setDeleteModalOpen(true)}>
                Delete
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {onCancel && (
              <Button type="button" variant="secondary" onClick={onCancel}>
                {cancelLabel}
              </Button>
            )}
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : submitLabel || (initialData ? "Update" : "Create")}
            </Button>
          </div>
        </div>
      </form>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={onDelete}
        tableName={tableName}
        recordId={formData[primaryKey]}
      />
    </>
  );
}

export default TableRecordForm;
